import { inject, type ObjectGraph } from '@jet/environment/dependencies';
import { ObjectGraphType } from '@jet-app/music-classical/dependencies/object-graph-type';
import { Bag } from '@jet-app/music-classical/dependencies/bag';
import { userInfo } from '@jet-app/music-classical/dependencies/subscription-status-coordinator';
import { normalizeLocale } from '@jet-app/music-classical/dependencies/locale';

/**
 * Use this utility function to fix up the objectGraph before using it for
 * library pages, etc. Anything that eventually calls `/me` (or similar) is
 * a good candidate for this. The rule specifically is this should be used on
 * any intent that calls an endpoint that always acts in the user's storefront.
 *
 * This utility adjusts the objectGraph to report the user's home storefront
 * and a supported language (best fit given the app's existing language) to avoid
 * errors from Media API when requested unsupported languages.
 *
 * Usage:
 *
 *   // Add this to the top of `perform`:
 *   async perform(intent: MyIntent, baseObjectGraph: ObjectGraph): Promise<...> {
 *       // From this point on, ONLY use objectGraph
 *       const objectGraph = fixLibraryObjectGraph(baseObjectGraph);
 *
 *       // ...
 *   }
 */
export function fixLibraryObjectGraph(objectGraph: ObjectGraph): ObjectGraph {
    const { storefront } = userInfo(objectGraph);

    if (!storefront) {
        throw new Error('called fixLibraryObjectGraph when signed out');
    }

    const bag = inject(ObjectGraphType.bag, objectGraph);
    // Using "as any" here as withDifferentLocale only exists on client Bag instance
    const bagImplementation = bag.bagImplementation as unknown as any;

    // Normalize the language based on the storefront in case the langauge of the
    // storefront that differs from the users' isn't supported.
    //
    // Ex: /de/browse with a us storefront will result in a language of en-US
    //     /mx/browse with a us storefront will result in a language of es-MX
    //
    // Note that this won't necessarily change the UI language, but will at least
    // use the correct locale data for any library API calls.
    const { language } = normalizeLocale(
        { storefront, language: Bag.languageTag(objectGraph) },
        objectGraph,
    );

    return objectGraph.adding(
        ObjectGraphType.bag,
        new Bag(bagImplementation.withDifferentLocale(storefront, language)),
    );
}
